import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchHospitalWards, AddWard } from '../../../helper/WardHelper';
import '../HospitalDashboard.css';
import { useLocation, useNavigate } from 'react-router-dom';
import AddWardModal from './AddWardModal';
import SignUpModal from '../signUpModal';
import moment from 'moment';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const formatDateTime = (value) => {
  if (!value) return '—';
  return moment(value).format('DD-MM-YYYY HH:mm');
};

const SuccessToast = ({ title, description }) => (
  <div className="hospital-toast" role="status" aria-live="polite">
    <span className="hospital-toast-icon" aria-hidden="true">✓</span>
    <div className="hospital-toast-text">
      <strong>{title}</strong>
      <span>{description}</span>
    </div>
  </div>
);

const WardCard = ({ ward, onClick }) => (
  <article
    className="hospital-card"
    onClick={() => onClick(ward)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(ward);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="hospital-card-head">
      <h3 className="hospital-card-name">{displayValue(ward.name)}</h3>
      <span className={`hospital-status-badge ${ward.isActive ? 'hospital-status-badge--active' : 'hospital-status-badge--inactive'}`}>
        {ward.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    <div className="hospital-card-details">
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">ID</span>
        <span className="hospital-card-detail-val">{displayValue(ward.id)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">CREATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(ward.createdAt)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">UPDATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(ward.updatedAt)}</span>
      </div>
    </div>

    <div className="hospital-card-foot">
      <span>View beds</span>
      <span aria-hidden="true">›</span>
    </div>
  </article>
);

const WardDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const hospitalId = queryParams.get('hospitalId');

  const [wards, setWards] = useState([]);
  const [hospital, setHospital] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddWardModalOpen, setIsAddWardModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const loadWards = () => {
    if (!hospitalId) return;
    fetchHospitalWards(hospitalId)
      .then((res) => {
        setWards(Array.isArray(res.data) ? res.data : []);
        setHospital(res.hospital || {});
      })
      .catch((err) => console.error('Error fetching wards:', err));
  };

  useEffect(() => {
    loadWards();
  }, [hospitalId]);

  useEffect(() => {
    if (showSuccessToast) {
      loadWards();
    }
  }, [showSuccessToast]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAddWard = (newWard) => {
    AddWard(newWard)
      .then(() => {
        setIsAddWardModalOpen(false);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      })
      .catch((err) => {
        console.error('Error adding ward:', err);
        alert(`Error adding ward: ${err}`);
      });
  };

  const filteredWards = useMemo(() => (
    (Array.isArray(wards) ? wards : []).filter((ward) => (
      ward?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  ), [wards, searchQuery]);

  const handleRowClick = (record) => {
    navigate(`/hospital-details/beds?wardId=${record.id}`);
  };

  return (
    <div className="hospital-page">
      {showSuccessToast && (
        <SuccessToast
          title="Ward added successfully"
          description="The ward has been added to this hospital."
        />
      )}

      <nav className="hospital-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate('/hospital-details/hospital-dashboard/')}>
          {displayValue(hospital?.name)}
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>Wards</span>
      </nav>

      <div className="hospital-header">
        <h3 className="hospital-title">Wards</h3>
      </div>

      <div className="hospital-filters-bar">
        <input
          ref={searchInputRef}
          type="text"
          className="hospital-search-input"
          placeholder="Search ward name..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <button
          type="button"
          className="hospital-btn hospital-btn--ghost"
          onClick={() => setIsSignUpModalOpen(true)}
        >
          Create New Sign Up
        </button>
        <button
          type="button"
          className="hospital-btn hospital-btn--ghost"
          onClick={() => navigate(`/hospital-details/hospital-user-credentials?hospitalId=${hospitalId}`, {
            state: { hospitalName: hospital.name },
          })}
        >
          Existing Credentials
        </button>
        <button
          type="button"
          className="hospital-btn hospital-btn--primary"
          onClick={() => setIsAddWardModalOpen(true)}
        >
          Add Ward
        </button>
      </div>

      <div className="hospital-stats-grid">
        <div className="hospital-stat-card hospital-stat-card--total">
          <span className="hospital-stat-label">TOTAL WARDS</span>
          <span className="hospital-stat-value">{filteredWards.length}</span>
        </div>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">Ward List</h2>
      </div>

      <div className="hospital-cards-scroll">
        {filteredWards.length === 0 ? (
          <div className="hospital-empty">No wards found.</div>
        ) : (
          <div className="hospital-cards-grid">
            {filteredWards.map((ward) => (
              <WardCard key={ward.id} ward={ward} onClick={handleRowClick} />
            ))}
          </div>
        )}
      </div>

      {isAddWardModalOpen && (
        <AddWardModal
          onClose={() => setIsAddWardModalOpen(false)}
          onSave={handleAddWard}
          hospitalId={hospitalId}
        />
      )}

      {isSignUpModalOpen && (
        <SignUpModal
          onClose={() => setIsSignUpModalOpen(false)}
          onSignUpSubmit={() => {}}
          hospitalName={hospital.name}
        />
      )}
    </div>
  );
};

export default WardDashboard;
